/**
 * What a variable's value looks like anywhere it is SHOWN rather than used.
 *
 * Every place that puts a saved variable's value on screen — the `{{…}}`
 * suggestion list, the hover hint on a `{{name}}` chip, the chip's own
 * `data-value`, Monaco's completion detail and hover — goes through here, so a
 * secret is masked in all of them or in none (card d21bee68, 26.09.2026:
 * typing `{{` in the Bearer token field put every token on screen, and
 * therefore into every screen share and recording).
 *
 * The mask is for DISPLAY. Substitution into the request the user sends still
 * reads the real value from the scope arrays; nothing here is on that path.
 */
import { lookupScopedVariable } from './variablePrecedence';

/** The same six dots the Variables table shows for a hidden secret. */
export const SECRET_MASK = '••••••';

/** `secret: true` is the one flag the lock icon in the Variables table sets. */
export function isSecretVariable(variable) {
  return variable?.secret === true;
}

/**
 * The value to render for a saved variable row.
 *
 * @param {{value?: any, secret?: boolean}} variable
 * @returns {string}
 */
export function displayValueOf(variable) {
  if (!variable) return '';
  if (isSecretVariable(variable)) return SECRET_MASK;
  return String(variable.value ?? '');
}

/**
 * The saved variable a `{{name}}` chip resolves to, for a hover or a chip.
 * Order: utils/variablePrecedence (folder > environment > collection >
 * workspace > global).
 *
 * @param {string} name
 * @param {{folder?: Array, collection?: Array, environment?: Array, workspace?: Array, global?: Array}} scopes
 *        `folder` is the flat nearest-first list from utils/folderVariables.
 * @returns {{value: string, display: string, type: string, folderId?: string, folderName?: string, collectionId?: string}|null}
 *          `display` is what may be rendered; `value` is the real one, for an editor only.
 */
export function lookupVariable(name, scopes = {}) {
  const hit = lookupScopedVariable(name, scopes);
  if (!hit) return null;
  const { scope: type, variable } = hit;
  const out = { value: variable.value ?? '', display: displayValueOf(variable), type };
  if (type === 'folder') {
    out.folderId = variable.folderId;
    out.folderName = variable.folderName;
    if (variable.collectionId != null) out.collectionId = variable.collectionId;
  }
  return out;
}
